import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, Filter, ChevronDown } from 'lucide-react';
import { Header } from '@/components/Header';
import { ToolCard } from '@/components/ToolCard';
import { ResourceCard } from '@/components/ResourceCard';
import { JobBoardCard } from '@/components/JobBoardCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useFavorites, useResourceFavorites, useJobBoardFavorites } from '@/hooks/useFavorites';
import { useTools } from '@/hooks/useTools';
import { useResources } from '@/hooks/useResources';
import { useJobBoards } from '@/hooks/useJobBoards';

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'resources', label: 'Resources' },
  { value: 'tools', label: 'Tools' },
  { value: 'job-boards', label: 'Job Boards' },
];

export default function Favorites() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['all']);
  
  const { favorites, clearFavorites } = useFavorites();
  const { resourceFavorites, clearResourceFavorites } = useResourceFavorites();
  const { jobBoardFavorites, clearJobBoardFavorites } = useJobBoardFavorites();
  
  const { data: tools = [] } = useTools();
  const { data: resources = [] } = useResources();
  const { data: jobBoards = [] } = useJobBoards();

  const favoriteTools = tools.filter(tool => favorites.includes(tool.id));
  const favoriteResources = resources.filter(resource => 
    resourceFavorites.includes(resource["Resource Name"])
  );
  const favoriteJobBoards = jobBoards.filter(jobBoard => 
    jobBoardFavorites.includes(jobBoard.id)
  );

  const filteredItems = useMemo(() => {
    const allItems = [
      ...favoriteResources.map(item => ({ type: 'resource' as const, data: item })),
      ...favoriteTools.map(item => ({ type: 'tool' as const, data: item })),
      ...favoriteJobBoards.map(item => ({ type: 'job-board' as const, data: item })),
    ];

    // Apply filter
    const filtered = selectedFilters.includes('all') 
      ? allItems 
      : allItems.filter(item => {
          if (selectedFilters.includes('resources') && item.type === 'resource') return true;
          if (selectedFilters.includes('tools') && item.type === 'tool') return true;
          if (selectedFilters.includes('job-boards') && item.type === 'job-board') return true;
          return false;
        });

    // Apply search if query exists
    if (searchQuery.trim()) {
      return filtered.filter(item => {
        const name = item.type === 'resource' 
          ? item.data["Resource Name"]
          : item.type === 'tool'
          ? item.data.name
          : item.data.name;
        return name.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    return filtered;
  }, [favoriteResources, favoriteTools, favoriteJobBoards, selectedFilters, searchQuery]);

  const totalFavorites = favoriteResources.length + favoriteTools.length + favoriteJobBoards.length;

  const clearAllFavorites = () => {
    clearFavorites();
    clearResourceFavorites();
    clearJobBoardFavorites();
  };

  const clearCurrentFilter = () => {
    if (selectedFilters.includes('all')) {
      clearAllFavorites();
    } else {
      if (selectedFilters.includes('resources')) clearResourceFavorites();
      if (selectedFilters.includes('tools')) clearFavorites();
      if (selectedFilters.includes('job-boards')) clearJobBoardFavorites();
    }
  };

  const getCurrentFilterCount = () => {
    if (selectedFilters.includes('all')) return totalFavorites;
    let count = 0;
    if (selectedFilters.includes('resources')) count += favoriteResources.length;
    if (selectedFilters.includes('tools')) count += favoriteTools.length;
    if (selectedFilters.includes('job-boards')) count += favoriteJobBoards.length;
    return count;
  };

  const toggleFilter = (filterValue: string) => {
    if (filterValue === 'all') {
      setSelectedFilters(['all']);
    } else {
      const newFilters = selectedFilters.includes(filterValue)
        ? selectedFilters.filter(f => f !== filterValue && f !== 'all')
        : [...selectedFilters.filter(f => f !== 'all'), filterValue];
      
      if (newFilters.length === 0) {
        setSelectedFilters(['all']);
      } else {
        setSelectedFilters(newFilters);
      }
    }
  };

  const getFilterLabel = () => {
    if (selectedFilters.includes('all')) return 'All';
    if (selectedFilters.length === 1) {
      const filter = selectedFilters[0];
      return filterOptions.find(opt => opt.value === filter)?.label || 'All';
    }
    return `${selectedFilters.length} selected`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        searchQuery={searchQuery} 
        onSearchChange={setSearchQuery}
        placeholder="Search favorites..."
      />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-text-primary">
              Your Favorites
            </h1>
            <p className="text-text-secondary mt-1">
              {totalFavorites} items saved
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center space-x-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-40 justify-between bg-background border-border"
                >
                  {getFilterLabel()}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2 bg-white dark:bg-gray-800 border border-border shadow-md z-50" align="start">
                <div className="space-y-2">
                  {filterOptions.map(option => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={option.value}
                        checked={selectedFilters.includes(option.value)}
                        onCheckedChange={() => toggleFilter(option.value)}
                        className="h-4 w-4"
                      />
                      <label 
                        htmlFor={option.value} 
                        className="text-sm text-text-primary cursor-pointer"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <div className="text-sm text-text-secondary">
              <span className="font-medium text-text-primary">{filteredItems.length}</span> items
            </div>
          </div>

          {getCurrentFilterCount() > 0 && (
            <Button
              variant="outline"
              onClick={clearCurrentFilter}
              className="text-text-tertiary hover:text-error border-border hover:border-error"
            >
              Clear {selectedFilters.includes('all') ? 'all' : selectedFilters.length > 1 ? 'selected' : (filterOptions.find(opt => opt.value === selectedFilters[0])?.label || 'selected')} favorites
            </Button>
          )}
        </div>

        {/* Active filters */}
        {(searchQuery || !selectedFilters.includes('all')) && (
          <div className="flex flex-wrap items-center gap-2 mb-6 p-4 bg-surface rounded-lg">
            <span className="text-sm text-text-secondary">Active filters:</span>
            
            {searchQuery && (
              <Badge variant="secondary" className="bg-accent-light text-accent">
                Search: "{searchQuery}"
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-1 hover:text-error transition-colors duration-fast"
                >
                  ×
                </button>
              </Badge>
            )}

            {!selectedFilters.includes('all') && selectedFilters.map(filterValue => (
              <Badge key={filterValue} variant="secondary">
                {filterOptions.find(opt => opt.value === filterValue)?.label}
                <button
                  onClick={() => toggleFilter(filterValue)}
                  className="ml-1 hover:text-error transition-colors duration-fast"
                >
                  ×
                </button>
              </Badge>
            ))}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedFilters(['all']);
              }}
              className="text-text-tertiary hover:text-error text-sm"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Results */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => {
              if (item.type === 'resource') {
                return <ResourceCard key={`resource-${item.data["Resource Name"]}-${index}`} resource={item.data} />;
              } else if (item.type === 'tool') {
                return <ToolCard key={`tool-${item.data.id}-${index}`} tool={item.data} />;
              } else if (item.type === 'job-board') {
                return <JobBoardCard key={`job-board-${item.data.id}-${index}`} jobBoard={item.data} />;
              }
              return null;
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="h-24 w-24 mx-auto mb-6 rounded-full bg-surface flex items-center justify-center">
                <Heart className="h-8 w-8 text-text-tertiary" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                {totalFavorites === 0 ? 'No favorites yet' : 'No matches found'}
              </h3>
              <p className="text-text-secondary mb-6">
                {totalFavorites === 0 
                  ? 'Save items you love and they\'ll show up here.'
                  : searchQuery 
                  ? 'Try adjusting your search or clearing filters.'
                  : `No ${selectedFilters.includes('all') ? '' : selectedFilters.join(', ') + ' '}favorites found.`
                }
              </p>
              <div className="flex gap-2 justify-center">
                <Button asChild variant="outline">
                  <Link to="/resources">Browse Resources</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/tools">Browse Tools</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/job-boards">Browse Job Boards</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}